import type { TOC } from '@ember/component/template-only';
import { Section } from 'tio-ui/components/layout';
import { t } from 'ember-intl';
import { Request } from 'tio-data';
import { notificationHistoryChartRequest } from 'tio-data/requests/profile-history';
import type { UserAggregateRow } from 'tio-data/requests/user';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type { UserExtension as UserModel } from 'tio-data/data/resources/user.ext';
import NotificationHistoriesTable from '../notification-histories/table.gts';
import Chart from 'tio-ui/components/chart';
import Button from 'tio-ui/components/button';

export interface ProfileNotificationHistoriesSignature {
  Args: {
    employee: EmployeeModel;
    user: UserModel;
    /** Maximum history rows per page; defaults to ten. */
    limit?: number;
  };
  Element: HTMLElement;
}

const chartData = (rows: UserAggregateRow[]) =>
  rows.map((row) => {
    const label = row.name || (typeof row.category === 'string' ? row.category : 'Unknown');
    const count = Number(row.value ?? row.count ?? 0);
    return {
      category: label
        .replace(/_NOTIFIER$/, '')
        .replace(/_/g, ' ')
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
        .trim(),
      value: Number.isFinite(count) ? count : 0,
    };
  });

const ProfileNotificationHistoriesComponent: TOC<ProfileNotificationHistoriesSignature> = <template>
  <Section @collapsed={{true}} ...attributes>
    <:header>{{t "common.email_history"}}</:header>
    <:body>
      <div class="tio-col-extra-loose lg:flex-row">
        {{#if @user.id}}
          <div class="lg:w-1/4 shrink-0">
            <Request @query={{notificationHistoryChartRequest @user.id}}>
              <:error as |_error features|>
                <div role="alert" class="tio-col-tight" data-test-notification-history-chart-error>
                  <p class="tio-fg-danger">{{t "common.records.load_error"}}</p>
                  <Button
                    @appearance="outlined"
                    @testHook="notification-history-chart__retry"
                    data-test-notification-history-chart-retry
                    {{on "click" features.retry}}
                  >
                    {{t "common.records.retry"}}
                  </Button>
                </div>
              </:error>
              <:loading>
                <div
                  class="tio-fg-muted text-center py-8"
                  data-test-notification-history-chart-loading
                >
                  <div class="text-sm">{{t "common.loading_chart"}}</div>
                </div>
              </:loading>
              <:content as |result|>
                {{#if result.data.length}}
                  <Chart
                    @data={{chartData result.data}}
                    @type="donut"
                    data-test-notification-history-chart
                    class="h-80"
                  />
                {{else}}
                  <div
                    class="tio-fg-muted text-center py-8"
                    data-test-notification-history-chart-empty
                  >
                    <div class="text-sm">{{t "common.no_chart_data"}}</div>
                  </div>
                {{/if}}
              </:content>
            </Request>
          </div>
        {{/if}}
        <div class="lg:flex-1 min-w-0 overflow-hidden">
          <div class="overflow-x-auto">
            <NotificationHistoriesTable
              @limit={{if @limit @limit 10}}
              @employee={{@employee}}
              @noteSubtypeFilter=""
            />
          </div>
        </div>
      </div>
    </:body>
  </Section>
</template>;

export default ProfileNotificationHistoriesComponent;
